<script lang="ts" module>
  const wordHistoryModal = new ModalState('word-history', { layer: 700 });
</script>

<script lang="ts">
  import Redo from '~icons/mdi/redo';
  import Word from './Word.svelte';
  import SortDropdown from './SortDropdown.svelte';
  import Chevron from '~/icons/Chevron.svelte';
  import WordShadows from './WordShadows.svelte';
  import { flyIn, shrinkPoints } from '~/animations.svelte';
  import { isClassic, type Match, type WordSort } from '../types';
  import ExpandButton from './buttons/ExpandButton.svelte';
  import SlidePanel from './SlidePanel.svelte';
  import Confetti from './Confetti.svelte';
  import { ModalState } from '~/lib/nav.svelte';
  import { settings } from '~/modals/Settings.svelte';
  import ActionButton from './ActionButton.svelte';
  import { wordToString } from '~/board/utils';
  import VirtualWordHistoryList from './VirtualWordHistoryList.svelte';
  import { getGame } from '~/store/GameContext.svelte';
  import { isPremium } from '~/lib/queries.svelte';
  import Premium from '~/icons/Premium.svelte';

  const game = $derived(getGame().current);
  let sort = $state<WordSort>('index');
  const premium = isPremium();

  const EMPTY_WORD: Match = {
    word: [],
    score: 0,
    axis: 'col',
    swaps: 0,
    length: 0,
    coords: [],
    stars: 0,
    alpha: '',
    intersectingIds: [],
  };

  const word = $derived(game.latestWord ?? EMPTY_WORD);

  const handleToggle = (open: boolean) => {
    if (open) {
      wordHistoryModal.push();
    } else {
      wordHistoryModal.pop();
    }
  };


  const key = $derived(wordToString(word));
</script>

{#if settings.confetti.current}
  <Confetti {word} />
{/if}
<SlidePanel open={wordHistoryModal.open} onToggle={handleToggle}>
  {#snippet title()}
    <h1 style:margin={0}>Word History</h1>
  {/snippet}
  {#snippet handle()}
    <div class="word-container">
      <ExpandButton open={wordHistoryModal.open} />
      {#key key}
        <Word
          word={word.word}
          highlighted={isClassic(game)
            ? 'green'
            : word.axis === 'col'
              ? 'daily-vertical'
              : 'daily-horizontal'}
        />
        <WordShadows word={word.word} />
        <div
          in:flyIn={{ y: 20, delay: 500 }}
          out:shrinkPoints
          class="word-score"
        >
          {#if isClassic(game)}
            +{word.score}
          {:else}
            {word.swaps}<Redo />
          {/if}
        </div>
      {/key}
    </div>
  {/snippet}
  {#snippet content({ scroll })}
    <VirtualWordHistoryList {sort} open={wordHistoryModal.open} {scroll} />
  {/snippet}
  {#snippet controls()}
    <ActionButton onClick={() => wordHistoryModal.pop()}>
      <Chevron down />Close</ActionButton
    >
    {#if premium.data}
      <SortDropdown
        bind:value={sort}
        options={[
          { value: 'index', label: 'Order' },
          { value: 'length', label: 'Length' },
          { value: 'score', label: 'Score' },
        ]}
      />
    {:else}
      <ActionButton outlink={false} href='?premium=t#sort'><Premium />Sort</ActionButton>
    {/if}
  {/snippet}
</SlidePanel>

<style lang="scss">
  .word-container {
    position: relative;
    display: grid;
    grid-auto-flow: column;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    :global(.expand-button) {
      width: 5rem;
    }
  }
  .word-score {
    position: absolute;
    right: 0;
    font-weight: bold;
    font-size: 1.25rem;
    display: grid;
    grid-auto-flow: column;
    align-items: center;
    gap: 0.5em;
  }
</style>
